import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Camera, QrCode, Users, ArrowRight, Smartphone, Zap, Download } from "lucide-react";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-foreground font-medium" data-testid="link-features">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-pricing">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-docs">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-blog">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" data-testid="button-login">
              Login
            </Button>
          </Link>
          <Link href="/dashboard">
            <Button size="sm" data-testid="button-signup">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function FeatureSection({
  title,
  description,
  highlight,
  icon: Icon,
  reversed = false,
  testId,
}: {
  title: string;
  description: string;
  highlight: string;
  icon: React.ElementType;
  reversed?: boolean;
  testId: string;
}) {
  return (
    <section className={`py-16 ${reversed ? "bg-muted/30" : ""}`} data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6">
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${reversed ? "md:flex-row-reverse" : ""}`}>
          <div className={reversed ? "md:order-2" : ""}>
            <h2 className="text-3xl font-semibold mb-4">{title}</h2>
            <p className="text-lg text-muted-foreground mb-6">{description}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 text-primary font-medium">
              <Zap className="w-4 h-4" />
              {highlight}
            </div>
          </div>
          <div className={`${reversed ? "md:order-1" : ""}`}>
            <Card>
              <CardContent className="pt-6 flex items-center justify-center min-h-[200px]">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-10 h-10 text-primary" />
                  </div>
                  <p className="text-muted-foreground text-sm">{title}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground">
              Docs
            </Link>
            <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground">
              Blog
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground">
              Terms
            </Link>
            <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground">
              Login
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Features() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main>
        <section className="py-16 text-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="text-features-headline">
              Everything You Need to Grow Your Reputation
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="text-features-subheadline">
              Simple tools designed for service businesses. No complexity, just results.
            </p>
          </div>
        </section>

        <FeatureSection
          title="The Review Flow"
          description="Your customers get a beautiful, mobile-first experience. They rate you with stars, and 5-star ratings go directly to Google Reviews. Low ratings stay private so you can address issues."
          highlight="Mobile-first. Loads in under 1 second."
          icon={Smartphone}
          testId="section-review-flow"
        />

        <FeatureSection
          title="Job Context That Converts"
          description="Attach photos of completed work to your review requests. When customers see the job you did, they remember the details and leave more thoughtful, detailed reviews."
          highlight="Jobs with photos get 2x more reviews"
          icon={Camera}
          reversed
          testId="section-job-context"
        />

        <FeatureSection
          title="QR Codes Everywhere"
          description="Generate unique QR codes for your business and each staff member. Print them, put them in vans, hand them to customers. Every scan is tracked back to the right person."
          highlight="Download print-ready PDFs for your vans"
          icon={QrCode}
          testId="section-qr-codes"
        />

        <FeatureSection
          title="Know Your Team"
          description="See which team members are generating the most reviews. Gamify your reputation management and reward your top performers. Turn reviews into a friendly competition."
          highlight="See who's actually asking for reviews"
          icon={Users}
          reversed
          testId="section-leaderboards"
        />

        <section className="py-16 text-center">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-semibold mb-6" data-testid="text-cta-headline">
              Ready to get started?
            </h2>
            <Link href="/dashboard">
              <Button size="lg" className="gap-2" data-testid="button-cta">
                Start Free
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
